import { backupWarning } from "@/lib/backupWarning";
import React, { useEffect, useState } from "react";
import { newVersionAvailable } from "@/lib/newVersion";
import { useInstall } from "@/lib/install";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";
import { loadPlatformVersion } from "@/lib/version";
import logo from "@/assets/HR.png";
import { PAGE_ACCESS } from "@/lib/pageAccess";
import DensityToggle from "@/components/DensityToggle";

import { NAV_GROUPS, NAV_GROUPS_ACTIVATION, NAV_GROUPS_SUPERADMIN } from "@/lib/nav";
import LayoutPurple from "@/components/LayoutPurple";
import LayoutFiord from "@/components/LayoutFiord";
import ThemePicker from "@/components/ThemePicker";
import { useTheme } from "@/lib/uiTheme";

export function LayoutNavy({ children }) {
  const { user, logout, hasRole, hasModule, allowed, activationRequired } = useAuth();
  const nav = useNavigate();
  const [unread, setUnread] = useState(0);
  const [platformVersion, setPlatformVersion] = useState("");
  useEffect(() => { loadPlatformVersion().then(setPlatformVersion); }, []);
  const pwa = useInstall();
  const [iosHelp, setIosHelp] = useState(false);
  const [newBuild, setNewBuild] = useState(false); // pe server există o interfață mai nouă decât cea încărcată în browser
  useEffect(() => {
    const check = () => newVersionAvailable().then(v => { if (v) setNewBuild(true); });
    check();
    const t = setInterval(check, 5 * 60 * 1000);
    window.addEventListener("focus", check);
    return () => { clearInterval(t); window.removeEventListener("focus", check); };
  }, [user?.id]);
  const [navOpen, setNavOpen] = useState(false); // meniul lateral ca sertar, pe ecrane înguste
  const [isClientInstall, setIsClientInstall] = useState(false);
  const [licenseAlert, setLicenseAlert] = useState(null); // null | "inactive" | "locked"
  const [updateBanner, setUpdateBanner] = useState(null); // null | {kind: error|ok|info, text, link?}
  const [collapsed, setCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem("sidebarCollapsed") || "{}"); } catch { return {}; }
  });
  const toggleGroup = (title) => {
    setCollapsed(prev => {
      const next = { ...prev, [title]: !prev[title] };
      try { localStorage.setItem("sidebarCollapsed", JSON.stringify(next)); } catch {}
      return next;
    });
  };

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try { const r = await api.get("/notifications?limit=1"); if(mounted) setUnread(r.data.unread || 0); } catch {}
    };
    if (activationRequired) return () => { mounted = false; };  // până la activare backend-ul refuză orice în afară de licență
    load();
    const t = setInterval(load, 30000);
    window.addEventListener("notifications-changed", load);   // marcare ca citit din pagina Notificări: contorul se actualizează imediat
    return () => { mounted = false; clearInterval(t); window.removeEventListener("notifications-changed", load); };
  }, [activationRequired]);

  // Pe o instalare de client, Adminul vede meniul „Licență" și o alertă dacă
  // instalarea nu e activată (angajatorul și licențele vin abia la activare).
  const isAdmin = hasRole("admin");
  useEffect(() => {
    const check = () => api.get("/config/deployment").then(r => {
      setIsClientInstall(!!r.data.is_client_install);
      if (r.data.is_client_install && isAdmin) {
        api.get("/activation/status").then(s => {
          setLicenseAlert(!s.data.activated ? "inactive" : s.data.locked ? "locked" : null);
        }).catch(() => {});
        // Starea actualizărilor, vizibilă pe orice pagină (inclusiv Panoul de control)
        if (!activationRequired) api.get("/updates/status").then(u => {
          const d = u.data || {};
          const recent = d.last_applied_at && (Date.now() - new Date(d.last_applied_at).getTime()) < 86400000;
          if (d.startup_error) setUpdateBanner({ kind: "error", text: "Actualizarea a întâmpinat o problemă la pornire. Contactează furnizorul." });
          else if (d.status === "failed") setUpdateBanner({ kind: "error", text: "Ultima actualizare a eșuat. Platforma rulează în continuare versiunea anterioară." });
          else if (d.update_stuck) setUpdateBanner({ kind: "error", text: "Actualizarea nu pornește: serviciul de actualizări de pe server nu răspunde (nu este instalat sau este oprit). Platforma rulează în continuare versiunea anterioară. Contactează administratorul serverului: pe server se rulează o dată `sudo ./kit/updater.sh`, apoi `sudo ./kit/setup-maintenance.sh`." });
          else if (d.status === "pending" || d.status === "downloading") setUpdateBanner({ kind: "info", text: "Actualizarea este în curs. Platforma repornește singură, în câteva minute." });
          else if (d.available) setUpdateBanner({ kind: "info", text: `Versiunea ${d.version} este disponibilă.`, link: "Preia update" });
          else if (d.status === "done" && recent) setUpdateBanner({ kind: "ok", text: `Actualizare aplicată: versiunea ${d.last_applied_version}. Totul funcționează normal.` });
          else if (backupWarning(d.backup_encryption)) setUpdateBanner({ kind: "warn", text: backupWarning(d.backup_encryption) });
          else setUpdateBanner(null);
        }).catch(() => {});
      }
    }).catch(() => {});
    check();
    // pagina Licență anunță activarea/sincronizarea, ca alerta să dispară imediat
    window.addEventListener("license-status-changed", check);
    return () => window.removeEventListener("license-status-changed", check);
  }, [isAdmin, activationRequired]);

  const doLogout = async () => { await logout(); nav("/login"); };
  const roleLabel = (user?.role_names || []).map(r => ({admin:"Admin",hr:"HR",manager:"Manager",employee:"Angajat",superadmin:"Superadmin"})[r]).join(" · ") || "—";

  return (
    <div className="app-shell">
      <div className={"nav-overlay" + (navOpen ? " show" : "")} onClick={() => setNavOpen(false)} data-testid="nav-overlay" />
      <aside className={"app-sidebar" + (navOpen ? " open" : "")} data-testid="app-sidebar">
        <NavLink to="/" onClick={() => setNavOpen(false)} style={{ padding: "20px 20px", borderBottom: "1px solid #F1F5F9", display:"flex", flexDirection:"column", alignItems:"center", gap:4, textDecoration:"none", cursor:"pointer" }} data-testid="sidebar-logo-link">
          <img src={logo} alt="Platforma de HR" style={{ width: 112, height: "auto" }} />
          <div style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 16, color: "var(--navy)", letterSpacing: "-0.02em" }}>Platforma de HR</div>
        </NavLink>
        <nav style={{ flex: 1, overflow: "auto", padding: "8px 10px 20px" }}>
          {(hasRole("superadmin") ? NAV_GROUPS_SUPERADMIN : activationRequired ? NAV_GROUPS_ACTIVATION : NAV_GROUPS).map(g => {
            const items = g.items.filter(it =>
              (hasRole("superadmin") || allowed(...(PAGE_ACCESS[it.to] ? [PAGE_ACCESS[it.to].roles, PAGE_ACCESS[it.to].perm] : [null, it.perm]))) &&
              (!it.clientInstallOnly || isClientInstall) &&
              (!it.module || hasModule(it.module))
            );
            if (items.length === 0) return null;
            const collapsible = g.title !== "Dashboard";
            const isOpen = !collapsible || !collapsed[g.title];
            return (
              <div key={g.title}>
                <div className="sidebar-section"
                  style={collapsible ? { cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between" } : undefined}
                  onClick={collapsible ? () => toggleGroup(g.title) : undefined}
                  data-testid={collapsible ? `sidebar-toggle-${g.title}` : undefined}>
                  <span>{g.title}</span>
                  {collapsible && <span style={{ fontSize: 10 }}>{isOpen ? "▾" : "▸"}</span>}
                </div>
                {isOpen && items.map(it => (
                  <NavLink key={it.to} to={it.to} end={it.to === "/" || items.some(o => o !== it && o.to.startsWith(it.to + "/"))} onClick={() => setNavOpen(false)}
                    data-testid={`nav-${it.to.replace(/\//g,"-").slice(1) || "home"}`}
                    className={({isActive}) => "sidebar-link" + (isActive ? " active" : "")}>
                    <span style={{ width: 18, textAlign: "center", fontSize: 14 }}>{it.icon}</span>
                    <span style={{flex:1}}>{it.label}</span>
                    {it.to === "/notifications" && unread > 0 && (
                      <span style={{ background:"var(--blue-primary)", color:"white", fontSize:10, padding:"1px 6px", borderRadius:99, fontWeight:700 }} data-testid="notif-badge">{unread}</span>
                    )}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>
        <div style={{ padding: 14, borderTop: "1px solid #F1F5F9" }}>
          <div style={{ fontSize: 13, fontWeight: 600 }} data-testid="current-user">{user?.name || user?.username}</div>
          <div style={{ fontSize: 10, color: "#64748B", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.04em" }}>{roleLabel}</div>
          {(pwa.canInstall || pwa.ios) && (
            <button className="btn btn-secondary" style={{ width: "100%", justifyContent: "center", marginBottom: 8 }} data-testid="install-app"
              onClick={() => (pwa.canInstall ? pwa.install() : setIosHelp(v => !v))}>📲 Instalează aplicația</button>
          )}
          {iosHelp && (
            <div style={{ fontSize: 12, color: "#475569", background: "#F1F5F9", borderRadius: 6, padding: "8px 10px", marginBottom: 8, lineHeight: 1.5 }} data-testid="ios-help">
              Pe iPhone/iPad: apasă <b>Distribuie</b> (pătratul cu săgeată) din Safari, apoi <b>Adaugă pe ecranul principal</b>.
            </div>
          )}
          <ThemePicker />
          <button className="btn btn-secondary" style={{ width: "100%", justifyContent: "center" }} onClick={doLogout} data-testid="logout-btn">Deconectare</button>
          {platformVersion && <div style={{ fontSize: 11, color: "#94A3B8", textAlign: "center", marginTop: 8 }} data-testid="platform-version">Versiunea {platformVersion}</div>}
        </div>
      </aside>
      <DensityToggle />
      <main className="app-main">
        <div className="mobile-topbar">
          <button type="button" className="menu-btn" aria-label="Deschide meniul" aria-expanded={navOpen}
            onClick={() => setNavOpen(o => !o)} data-testid="menu-toggle">☰</button>
          <img src={logo} alt="" style={{ height: 28, width: "auto" }} />
          <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 15, color: "var(--navy)" }}>Platforma de HR</span>
        </div>
        {licenseAlert && (
          <div className="app-banner" style={{ background: "#FEF3C7", borderBottom: "1px solid #FCD34D", fontSize: 13, color: "#92400E" }} data-testid="license-alert">
            {licenseAlert === "inactive"
              ? "Instalarea nu este încă activată — angajatorul și licențele apar automat după activare. "
              : "Licența este blocată: sincronizarea cu furnizorul eșuează de prea mult timp. "}
            <NavLink to="/admin/license" style={{ fontWeight: 700, color: "#92400E" }}>
              {licenseAlert === "inactive" ? "Activează licența" : "Vezi detalii"}
            </NavLink>
          </div>
        )}
        {newBuild && (
          <div className="app-banner" style={{ background: "var(--tint-50)", borderBottom: "1px solid var(--tint-200)", fontSize: 13, color: "var(--navy-3)" }} data-testid="new-build-banner">
            Platforma a fost actualizată, dar această pagină încă rulează versiunea veche.{" "}
            <button className="btn btn-secondary" style={{ padding: "2px 10px", marginLeft: 6 }} onClick={() => window.location.reload()}>Reîncarcă pagina</button>
          </div>
        )}
        {updateBanner && (() => {
          const c = { error: ["#FEF2F2", "#FCA5A5", "#991B1B"], ok: ["#F0FDF4", "#86EFAC", "#14532D"], info: ["var(--tint-50)", "var(--tint-200)", "var(--navy-3)"], warn: ["#FFFBEB", "#F59E0B", "#78350F"] }[updateBanner.kind];
          return (
            <div className="app-banner" style={{ background: c[0], borderBottom: `1px solid ${c[1]}`, fontSize: 13, color: c[2] }} data-testid="update-banner">
              {updateBanner.text}{" "}
              {updateBanner.link && <NavLink to="/admin/license" style={{ fontWeight: 700, color: c[2] }}>{updateBanner.link}</NavLink>}
            </div>
          );
        })()}
        <div className="app-content" style={{ maxWidth: 1600 }}>{children}</div>
      </main>
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex-between fade-up page-header" style={{ marginBottom: 22, gap: 12, flexWrap: "wrap" }}>
      <div>
        <h1 style={{ fontSize: 26, fontWeight: 800, margin: 0, color: "var(--navy)", letterSpacing: "-0.02em" }}>{title}</h1>
        {subtitle && <div style={{ color: "#64748B", marginTop: 4, fontSize: 13 }}>{subtitle}</div>}
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>{actions}</div>
    </div>
  );
}
export function StatusBadge({ status, override }) {
  const map = { draft: ["badge-draft","Ciornă"], submitted: ["badge-submitted","În așteptare"],
    approved: ["badge-approved","Aprobat"], rejected: ["badge-rejected","Respins"],
    active: ["badge-active","Activă"], closed: ["badge-closed","Închisă"], archived: ["badge-archived","Arhivată"],
    expired: ["badge-rejected","Expirată"], suspended: ["badge-draft","Suspendată"], replaced: ["badge-inactive","Înlocuită"] };
  const [cls, label] = map[status] || ["badge-draft", status];
  return <span className={`badge ${cls}`} data-testid={`status-${status}`}>{override || label}</span>;
}
export function Modal({ title, children, onClose, width }) {
  // Nu se închide la click în afara modalului — doar butonul X sau Cancel închid.
  // Motiv: utilizatorii pierdeau date atunci când selectau text și trăgeau cu mouse-ul în afara ferestrei.
  // Ferestrele sunt cu ~35% mai late decât lățimea cerută (maxim 1180px): câmpurile se așază câte 3 pe rând.
  const wide = Math.min(Math.round((width || 640) * 1.35), 1180);
  return (
    <div className="modal-backdrop">
      <div className="modal" style={{ maxWidth: wide }}>
        <div className="modal-head">
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "var(--navy)" }}>{title}</h3>
          <button onClick={onClose} data-testid="modal-close" style={{ border: "none", background: "none", cursor: "pointer", fontSize: 22, color: "#64748B" }}>×</button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}
export function Toast({ msg, type = "info" }) {
  if (!msg) return null;
  const styles = { info: { bg: "var(--sky-100)", color: "var(--sky-700)", border: "var(--sky-300)" },
    success: { bg: "var(--tint-100)", color: "var(--navy-3)", border: "var(--tint-400)" },
    error: { bg: "#FEE2E2", color: "#991B1B", border: "#FCA5A5" } };
  const s = styles[type] || styles.info;
  // Fixed + z-index peste orice modal (.modal-backdrop e 60) — altfel un
  // mesaj de eroare declanșat cât un pop-up de adăugare/editare e deschis
  // rămâne ascuns în spatele lui.
  return <div style={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)",
    zIndex: 200, minWidth: 260, maxWidth: "min(520px, calc(100vw - 40px))",
    background: s.bg, color: s.color, border: `1px solid ${s.border}`, padding: "12px 18px",
    borderRadius: 8, fontSize: 13, boxShadow: "0 10px 30px rgba(15,23,42,0.2)" }} data-testid="toast">{msg}</div>;
}
export function ExportButtons({ basePath, perm }) {
  // Descărcare directă (navigare pe link) — cookie-ul de sesiune se trimite normal,
  // fără nevoie de fetch/blob; răspunsul are Content-Disposition: attachment.
  // `perm` = dreptul de export; fără el, butoanele rămân vizibile, dar blocate.
  const { has } = useAuth();
  const ok = !perm || has(perm);
  const btn = (href, label, tid) => ok
    ? <a className="btn btn-secondary" href={href} data-testid={tid}>{label}</a>
    : <button className="btn btn-secondary" disabled title="Nu ai dreptul pentru această acțiune" data-testid={tid}>{label}</button>;
  return (
    <div style={{ display: "flex", gap: 6 }}>
      {btn(`${API}${basePath}/export.xlsx`, "Export XLSX", "export-xlsx")}
      {btn(`${API}${basePath}/export.csv`, "Export CSV", "export-csv")}
    </div>
  );
}
export function EmptyState({ text }) {
  return <div style={{ padding: 40, textAlign: "center", color: "#94A3B8", fontSize: 14 }}>{text}</div>;
}
export function PasswordInput({ value, onChange, className, testId, guard, ...rest }) {
  const [visible, setVisible] = useState(false);
  const [armed, setArmed] = useState(!guard);   // guard = „doar citire" până la primul click (anti-completare automată)
  return (
    <div style={{ position: "relative" }}>
      <input type={visible ? "text" : "password"} className={className || "input"}
        value={value} onChange={onChange} data-testid={testId} style={{ paddingRight: 38 }} {...rest}
        readOnly={!armed} onFocus={(e) => { e.target.readOnly = false; setArmed(true); rest.onFocus && rest.onFocus(e); }} />
      <button type="button" onClick={()=>setVisible(v=>!v)} aria-label={visible ? "Ascunde parola" : "Arată parola"}
        data-testid={testId ? `${testId}-toggle` : undefined}
        style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
          border: "none", background: "none", cursor: "pointer", fontSize: 15, color: "#64748B", padding: 4 }}>
        {visible ? "🙈" : "👁"}
      </button>
    </div>
  );
}

/** Coaja aplicației, în aspectul ales (fiord, navy sau purple); fiecare aspect are propria coajă, nu doar alte culori. */
export default function Layout({ children }) {
  const theme = useTheme();
  if (theme === "fiord") return <LayoutFiord>{children}</LayoutFiord>;
  if (theme === "purple") return <LayoutPurple>{children}</LayoutPurple>;
  return <LayoutNavy>{children}</LayoutNavy>;
}
